import katex from 'katex'
import 'katex/dist/katex.min.css'
import { sanitizeHtml } from '@/libs/sanitize'

function decodeHtmlEntities(text = '') {
  return String(text)
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#x27;|&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
}

function escapeHtml(text = '') {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function sanitizeMathExpression(expr = '') {
  return decodeHtmlEntities(expr)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/?[^>]+>/g, ' ')
    .replace(/`/g, '')
    .replace(/\*\*/g, '')
    .replace(/__/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function renderFormula(expr, { displayMode = false, fallbackWrapper } = {}) {
  const sanitized = sanitizeMathExpression(expr)

  try {
    return katex.renderToString(sanitized, {
      displayMode,
      throwOnError: true,
      strict: 'ignore',
    })
  } catch {
    return typeof fallbackWrapper === 'function'
      ? fallbackWrapper(sanitized)
      : escapeHtml(sanitized)
  }
}

/**
 * Renders LaTeX expressions in a string to HTML.
 * Supports:
 *  - Display math: $$ ... $$ or \[ ... \]
 *  - Inline math:  $ ... $  or \( ... \)
 *
 * Returns an HTML string with rendered formulas.
 */
export function renderLatex(text) {
  if (!text || typeof text !== 'string') return text || ''

  // Display math: $$ ... $$
  let result = text.replace(/\$\$([\s\S]+?)\$\$/g, (_, expr) => renderFormula(expr, {
    displayMode: true,
    fallbackWrapper: content => `<div class="katex-fallback">${escapeHtml(content)}</div>`,
  }))

  // Display math: \[ ... \]
  result = result.replace(/\\\[([\s\S]+?)\\\]/g, (_, expr) => renderFormula(expr, {
    displayMode: true,
    fallbackWrapper: content => `<div class="katex-fallback">${escapeHtml(content)}</div>`,
  }))

  // Inline math: $ ... $ (but not $$)
  result = result.replace(/(?<!\$)\$(?!\$)(.+?)(?<!\$)\$(?!\$)/g, (_, expr) => renderFormula(expr, {
    displayMode: false,
    fallbackWrapper: content => `<span class="katex-fallback">${escapeHtml(content)}</span>`,
  }))

  // Inline math: \( ... \)
  result = result.replace(/\\\((.+?)\\\)/g, (_, expr) => renderFormula(expr, {
    displayMode: false,
    fallbackWrapper: content => `<span class="katex-fallback">${escapeHtml(content)}</span>`,
  }))

  // Markdown bold & italic (applied after LaTeX to avoid breaking formulas)
  result = result.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  result = result.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')

  return sanitizeHtml(result)
}

/**
 * Checks if a string contains any LaTeX expressions
 */
export function hasLatex(text) {
  if (!text) return false
  return /\$[^$]+\$|\\\(.*?\\\)|\\\[[\s\S]*?\\\]/.test(text)
}
